import { useSupabaseClient } from '#imports'

interface Profile {
  full_name: string
  avatar_url: string | null
  phone: string
  email: string
  website: string
  username: string
}

export const useUserProfile = () => {
  const client = useSupabaseClient()
  const { t } = useI18n()
  const user = useSupabaseUser()

  const profile = useState<Profile>('profile', () => ({
    full_name: '',
    avatar_url: null,
    phone: '',
    email: '',
    website: '',
    username: ''
  }))

  const error = ref('')
  const isLoading = ref(false)

  const loadProfile = async () => {
    try {
      isLoading.value = true
      error.value = ''

      const { data, error: fetchError } = await client
        .from('profiles')
        .select('*')
        .eq('id', user.value?.id)
        .single()

      if (fetchError) {
        return
      }

      if (data) {
        profile.value = {
          full_name: data.full_name || '',
          avatar_url: data.avatar_url,
          phone: data.phone || '',
          email: user.value?.email || '',
          website: data.website || '',
          username: data.username || ''
        }
      }
    } catch (e) {
      error.value = t('profile.messages.loadError')
      console.error('Load profile error:', e)
    } finally {
      isLoading.value = false
    }
  }

  const saveProfile = async () => {
    try {
      isLoading.value = true
      error.value = ''

      // Nejprve zkontrolujeme, zda profil existuje
      const { data: existingProfile } = await client
        .from('profiles')
        .select('id')
        .eq('id', user.value?.id)
        .single()

      let result
      if (existingProfile) {
        // Update existujícího profilu
        result = await client
          .from('profiles')
          .update({
            full_name: profile.value.full_name,
            phone: profile.value.phone,
            avatar_url: profile.value.avatar_url,
            updated_at: new Date().toISOString(),
            website: profile.value.website,
            username: profile.value.username.trim() || null
          })
          .eq('id', user.value?.id)
      } else {
        // Vytvoření nového profilu
        result = await client
          .from('profiles')
          .insert({
            id: user.value?.id,
            full_name: profile.value.full_name,
            phone: profile.value.phone,
            avatar_url: profile.value.avatar_url,
            updated_at: new Date().toISOString(),
            website: profile.value.website,
            username: profile.value.username.trim() || null
          })
      }

      if (result.error) {
        // Specifická chyba pro duplicitní username
        if (result.error.code === '23505' && result.error.message.includes('profiles_username_key')) {
          error.value = t('profile.messages.usernameExists') || 'Tento username už je obsazený. Zvolte prosím jiný.'
        } else {
          error.value = t('profile.messages.updateError')
        }
        console.error('Profile save error:', result.error)
        return
      }
    } catch (e) {
      error.value = t('profile.messages.updateError')
      console.error('Save profile error:', e)
    } finally {
      isLoading.value = false
    }
  }

  return {
    profile,
    error,
    isLoading,
    loadProfile,
    saveProfile
  }
}
